import React, { memo, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { FormState } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/AddHypotheses/AgentHypothesisModal/AgentHypothesisModal';
import DateSelect from './components/DateSelect';
import { useGetHrSimulationOfficialGradeHypotheses } from './helpers/hooks/simulationOfficialGrade';
import { usePostAgentGrade } from './helpers/hooks/agentGrade';
import TbiCalculation from './components/TBICalculation';
import GradeSelect from './components/GradeSelect';
import EchelonSelect from './components/EchelonSelect';
import OfficialGradeModal from './components/OfficialGradeGrid/OfficialGradeGrid';
import { useTranslation } from 'react-i18next';

type Props = {
    hrSimulation: HrSimulation;
    isUpdateEnabled?: boolean;
    agentData: SimulationAgentData;
    recurrence: RecurrenceEnum;
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>;
};

const GradeAdvancementHypothesisModalContent = ({
    hrSimulation,
    agentData,
    recurrence,
    formState,
    setFormState,
    isUpdateEnabled = true,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const {
        data: hrSimulationOfficialGradeResponse,
        isLoading: isHrSimulationOfficialGradeLoading,
    } = useGetHrSimulationOfficialGradeHypotheses(hrSimulation.id);

    const hrSimulationOfficialGradeData =
        hrSimulationOfficialGradeResponse || [];

    const { data: agentGradeResponse, isLoading: isAgentGradeLoading } =
        usePostAgentGrade({
            recurrence,
            orgChartHrSoftwareId: agentData.orgChartHrSoftwareId,
            jobNumber: agentData.jobNumber,
            agentSoftwareId: agentData.agentSoftwareId,
            mantyStatus: agentData.mantyStatus,
            simulationId: hrSimulation.id,
        });

    const agentGrade = agentGradeResponse || {
        grade: '',
        echelon: '',
        majoredIndex: 0,
    };

    const isLoading = isAgentGradeLoading || isHrSimulationOfficialGradeLoading;

    const gradeData = hrSimulationOfficialGradeData.find(
        (el) => el.grade === formState.grade,
    );

    const echelonKeys = gradeData ? Object.keys(gradeData.gridEchelons) : [];
    const newMajoredIndex =
        hrSimulationOfficialGradeData.find((el) => el.grade === formState.grade)
            ?.gridEchelons[formState.echelon]?.majoredIndex || '';

    const onGradeChange = (grade: string) => {
        setFormState((prevState) => ({
            ...prevState,
            grade,
        }));
    };
    const onEchelonChange = (echelon: string) => {
        setFormState((prevState) => ({
            ...prevState,
            echelon,
        }));
    };
    const onStartDateChange = (gradeStartDate: SimulationDateString) => {
        setFormState((prevState) => ({
            ...prevState,
            gradeStartDate,
        }));
    };
    const onSalaryChange = (gradeSalary: number) => {
        setFormState((prevState) => ({
            ...prevState,
            gradeSalary,
        }));
    };
    const onChanges = (hasGradeChanges: boolean) => {
        setFormState((prevState) => ({
            ...prevState,
            hasGradeChanges,
        }));
    };

    return (
        <Box>
            <GradeSelect
                isLoading={isLoading}
                agentGrade={agentGrade}
                hrSimulationOfficialGradeData={hrSimulationOfficialGradeData}
                grade={formState.grade}
                onGradeChange={onGradeChange}
                onChanges={onChanges}
                isUpdateEnabled={isUpdateEnabled}
                onEchelonChange={onEchelonChange}
            />
            <Flex align="center" justify="end" alignItems="center">
                <Button
                    size="small"
                    onClick={() => setShowModal(true)}
                    variant="text"
                    sx={{ fontSize: '12px' }}
                >
                    {t(
                        'humanResources.simulation.gradeHypothesis.seeOfficialGrade',
                    )}
                </Button>
                {showModal && (
                    <OfficialGradeModal
                        showModal={showModal}
                        hrSimulationOfficialGradeData={
                            hrSimulationOfficialGradeData
                        }
                        setShowModal={setShowModal}
                        isOfficialGradeDataLoading={
                            isHrSimulationOfficialGradeLoading
                        }
                    />
                )}
            </Flex>

            <Grid container spacing={4} sx={{ paddingTop: '24px' }}>
                <Grid item xs={6}>
                    <EchelonSelect
                        isLoading={isLoading}
                        echelon={formState.echelon}
                        onEchelonChange={onEchelonChange}
                        onSalaryChange={onSalaryChange}
                        onChanges={onChanges}
                        gradeData={gradeData}
                        echelonKeys={echelonKeys}
                        isUpdateEnabled={isUpdateEnabled}
                    />
                </Grid>
                <Grid item xs={6}>
                    <DateSelect
                        isLoading={isLoading}
                        gradeStartDate={formState.gradeStartDate}
                        onStartDateChange={onStartDateChange}
                        onChanges={onChanges}
                        isUpdateEnabled={isUpdateEnabled}
                        hrSimulation={hrSimulation}
                    />
                </Grid>
            </Grid>

            {!!formState.gradeSalary && !!newMajoredIndex && (
                <TbiCalculation
                    gradeSalary={formState.gradeSalary}
                    newMajoredIndex={newMajoredIndex}
                    isLoading={isLoading}
                />
            )}
        </Box>
    );
};

export default memo(GradeAdvancementHypothesisModalContent);
